import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Card, Button, Typography, Row, Col, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getUsers } from '../../api/users';
import type { User } from '../../types/users';
import UsersTable from '../../components/features/users/UsersTable';
import UserModal from '../../components/features/users/UserModal';

const { Title } = Typography;
const PAGE_SIZE = 10;

type LastDocs = { [page: number]: any };

const Users: React.FC = () => {
  const { t } = useTranslation(['users', 'common']);
  const navigate = useNavigate();

  const [editingUser, setEditingUser] = useState<User | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [lastDocs, setLastDocs] = useState<LastDocs>({});
  const [hasNext, setHasNext] = useState<boolean>(false);

  const { data = { users: [], lastDoc: undefined }, isLoading } = useQuery<{ users: User[]; lastDoc?: any }>({
    queryKey: ['users', page, lastDocs[page - 1]],
    queryFn: async ({ queryKey }) => {
      const [_key, _page, lastDoc] = queryKey as [string, number, any];
      return getUsers(PAGE_SIZE, lastDoc);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    if (data.lastDoc) {
      setLastDocs(prev => ({ ...prev, [page]: data.lastDoc }));
      setHasNext(!!data.lastDoc && data.users.length === PAGE_SIZE);
    }
  }, [data, page]);

  const users: User[] = data.users;

  const handleAdd = useCallback(() => {
    setEditingUser(undefined);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((user: User) => {
    navigate(`/users/${user.id}`, { state: { user } });
  }, [navigate]);

  const pagination = useMemo(() => ({
    current: page,
    pageSize: PAGE_SIZE,
    total: hasNext ? (page + 1) * PAGE_SIZE : page * PAGE_SIZE,
    onChange: (newPage: number) => setPage(newPage),
  }), [page, hasNext]);

  return (
    <div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4}>{t('users:title')}</Title>
            </Col>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                {t('users:add')}
              </Button>
            </Col>
          </Row>
          <UsersTable
            users={users}
            loading={isLoading}
            pagination={pagination}
            onEdit={handleEdit}
            onView={handleView}
            onTableChange={pagination.onChange}
          />
        </Space>
      </Card>
      <UserModal
        visible={isModalOpen}
        user={editingUser}
        onCancel={() => setIsModalOpen(false)}
        onSuccess={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default Users; 